<template>
  <SogForm
    ref="_form"
    :validations="_validations"
  >
    <!--
        <SogFormField v-show="_accountStore.hasAccountingLicense()">
    -->
    <SogFormField v-show="_accountStore.hasAction(Action.OrderingBlocking)">
      <SogSwitch
        v-model="_orderingBlocking"
        placeholder="Bestellmöglichkeit gesperrt"
      />
    </SogFormField>
    <SogFormField>
      <SogDropDownList
        v-model="_orderTypeId"
        placeholder="Rechnungstyp"
        :data-source="getOrderTypes()"
      />
    </SogFormField>
    <div>
      <SogFormField>
        <SogTextBox
          v-model="_invoiceCircle"
          placeholder="Rechnungsreferenznummer"
          name="Rechnungsreferenznummer"
        />
      </SogFormField>
      <SogFormField v-show="_accountStore.hasAccountingLicense()">
        <SogTextBox
          v-model="_email"
          placeholder="Rechnungs-Email"
          name="email"
        />
      </SogFormField>
      <SogFormField v-show="_accountStore.hasAccountingLicense()">
        <SogSwitch
          v-model="_withoutCreditLimit"
          placeholder="ohne Kreditlimit"
        />
      </SogFormField>
      <SogFormField v-show="_orderTypeId === 'AccumulativeInvoice'">
        <SogSwitch
          v-model="_automaticCancelationInvoicing"
          placeholder="Automatische Stornoabrechnung"
        />
      </SogFormField>

      <SogFormField v-show="_orderTypeId === 'AccumulativeInvoice'">
        <SogSwitch
          v-model="_cancellationAllowedWithoutPayment"
          placeholder="Stornogenehmigung ohne Bezahlung"
        />
      </SogFormField>

      <SogFormField v-show="_accountStore.hasAccountingLicense()&&!_withoutCreditLimit">
        <SogNumericTextBox
          v-model="_creditLimit"
          placeholder="Betrag, €"
        />
      </SogFormField>
      <SogFormField>
        <SogTextBox
          v-model="_invoiceDueDate"
          placeholder="Rechnungsfälligkeit"
        />
      </sogformfield>
    </div>
    <SogFormField v-show="_orderTypeId==='AccumulativeInvoice'">
      <SogDatePicker
        v-model="_nextInvoiceDate"
        width="100%"
        format="dd.MM.yyyy HH:mm"
        placeholder="Nächste Rechnung am"
      />
      <SogScheduleSerie
        :next-invoice-date="_nextInvoiceDate"
      />
    </sogformfield>
  </SogForm>
</template>

<script setup lang="ts">

import SogForm from "@/shared/ui/SogForm.vue";
import SogFormField from "@/shared/ui/SogFormField.vue";
import SogDropDownList from "@/shared/ui/SogDropDownList.vue";
import PersonDetails from "@/shared/model/personDetails";
import {getOrderTypes} from "@/shared/model/orderType";
import {ref, watch} from "vue";
import PersonDetailsId from "@/shared/model/personDetailsId";
import SogSwitch from "@/shared/ui/SogSwitch.vue";
import SogNumericTextBox from "@/shared/ui/SogNumericTextBox.vue";
import SogTextBox from "@/shared/ui/SogTextBox.vue";
import SogFormValidationRule from "@/shared/ui/types/SogFormValidationRule";
import {useAccountStore} from "@/stores/accountStore";
import SogDatePicker from "@/shared/ui/SogDatePicker.vue";
import SogScheduleSerie from "@/shared/ui/SogScheduleSerie.vue";
import {Action} from "@/shared/model/Action";

interface Props {
  details?: PersonDetails
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const props = withDefaults(defineProps<Props>(), {
    details: undefined
  }
)

const _accountStore = useAccountStore();


const _form = ref<InstanceType<typeof SogForm> | null>(null)


const _orderTypeId = ref("")
const _withoutCreditLimit = ref<boolean>(false)
const _creditLimit = ref<number | undefined>(0)
const _email = ref<string | undefined>(undefined)
const _invoiceCircle = ref<string | undefined>(undefined)
const _nextInvoiceDate = ref<Date | undefined>(undefined)
const _invoiceDueDate = ref<string | undefined>(undefined)
const _orderingBlocking = ref<boolean | undefined>(false)
const _automaticCancelationInvoicing = ref<boolean | undefined>(false)
const _cancellationAllowedWithoutPayment = ref<boolean | undefined>(false)

const _validations: SogFormValidationRule[] = [
  {name: 'email', rules: {email: true}},
]

watch(props, () => {
  _orderTypeId.value = props.details?.getValue(PersonDetailsId.OrderTypeId)
  _email.value = props.details?.getValue(PersonDetailsId.OrderEmail)
  _invoiceCircle.value = props.details?.getValue(PersonDetailsId.InvoiceCircle)
  _creditLimit.value = props.details?.getNumberValue(PersonDetailsId.CreditLimit)
  _nextInvoiceDate.value = props.details?.getValue(PersonDetailsId.OrderScheduleNext)
  _invoiceDueDate.value = props.details?.getValue(PersonDetailsId.InvoiceDueDate)
  _withoutCreditLimit.value = props.details?.getBooleanValue(PersonDetailsId.WithoutCreditLimit) ?? false
  _orderingBlocking.value = props.details?.getBooleanValue(PersonDetailsId.OrderingBlocking) ?? false
  _automaticCancelationInvoicing.value = props.details?.getBooleanWithDefaultValue(PersonDetailsId.AutomaticCancelationInvoicing, true)
  _cancellationAllowedWithoutPayment.value = props.details?.getBooleanValue(PersonDetailsId.CancellationAllowedWithoutPayment) ?? false
}, {immediate: true})


function validate(): boolean {
  return _form.value?.validate() ?? false;
}

function getDetails(): PersonDetails {
  const res = new PersonDetails(props?.details?.getValues())
  res.setValue(PersonDetailsId.OrderTypeId, _orderTypeId.value)
  res.setValue(PersonDetailsId.OrderEmail, _email.value)
  res.setValue(PersonDetailsId.InvoiceCircle, _invoiceCircle.value)
  res.setValue(PersonDetailsId.InvoiceDueDate, _invoiceDueDate.value)
  res.setNumberValue(PersonDetailsId.CreditLimit, _creditLimit.value ?? 0)
  res.setBooleanValue(PersonDetailsId.WithoutCreditLimit, _withoutCreditLimit.value ?? false)
  res.setBooleanValue(PersonDetailsId.OrderingBlocking, _orderingBlocking.value ?? false)
  res.setBooleanValue(PersonDetailsId.AutomaticCancelationInvoicing, _automaticCancelationInvoicing.value ?? true)
  res.setBooleanValue(PersonDetailsId.CancellationAllowedWithoutPayment, _cancellationAllowedWithoutPayment.value ?? false)
  return res;
}

defineExpose({
  getDetails, validate
})

</script>

<style scoped>

</style>
